import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import {
  Button,
  FormControl,
  Grid,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Snackbar,
  Typography,
} from "@material-ui/core";
import { Slider } from "@mui/material";
import { Alert } from "@material-ui/lab";
import { useCompany } from "../../../../contexts/CompanyContext";
import { fetchProductStatusses } from "./productsSB.functions";
import BackLink from "../../../shared/BackLink";
import productFormValidation from "./productFormValidation";

/**
 * EditProduct component allows editing of a product's details.
 *
 * @component
 * @param {object} props - The component props.
 * @param {object} props.match - The match object from react-router.
 * @returns {React.Element} The rendered component.
 *
 * @example
 * <EditProduct match={match} />
 */
export default function EditProduct({ match }) {
  const history = useHistory();

  const productId = parseInt(match.params.product);
  const supplierId = parseInt(match.params.supplier);

  const { productTypes, upsertProduct, getProduct } = useCompany();
  const [loading, setLoading] = useState(true);

  const [alertMessage, setAlertMessage] = useState({
    open: false,
    severity: "info",
    message: "",
  });

  const [productStatusses, setProductStatusses] = useState([]);

  const [formValues, setFormValues] = useState({
    com_ogr_percentage_receivable: 0,
    com_once_off_receivable_vat_excl: 0,
    customer_price_vat_excl: 0,
    display_name: "",
    product_description: "",
    refs_product_status_id: 1,
    refs_product_type_id: 1,
    revenue_points: 0,
  });
  // HANDLERS

  // handle changes on form values

  const handleAlert = (message, severity) => {
    setAlertMessage({ message, severity, open: true });
  };

  const closeAlert = () => {
    setAlertMessage(prev => ({ ...prev, open: false }));
  };

  const handleChange = event => {
    const { name, value } = event.target;
    setFormValues(prevFormValues => ({
      ...prevFormValues,
      [name]: value,
    }));
  };

  const sanitizeNumberInput = (value, allowDecimal = false) => {
    const regex = allowDecimal ? /^\d*(\.\d{0,2})?$/ : /^[0-9]\d*$/;
    const digitRegex = /^[0-9]/;
    const cleanedValue = value.trim();

    if (cleanedValue === "") {
      return { value: null, error: 0 };
    }

    // Check if first character is valid (number)
    if (!digitRegex.test(cleanedValue[0])) {
      return { value: "0", error: 0 };
    }

    if (regex.test(cleanedValue)) {
      const parsedValue = allowDecimal
        ? parseFloat(cleanedValue)
        : parseInt(cleanedValue, 10);

      if (!Number.isNaN(parsedValue) && cleanedValue >= 0) {
        return { value: parsedValue, error: 0 };
      }
    }

    return { value: cleanedValue, error: "Please enter a valid number" };
  };

  const handleNumber = (event, allowDecimal = false) => {
    const { name, value } = event.target;

    setFormValues(prev => {
      const sanitizedValue = sanitizeNumberInput(value, allowDecimal);

      if (sanitizedValue.error === 0) {
        return { ...prev, [name]: sanitizedValue.value };
      }
      handleAlert(sanitizedValue.error, "error");
      return prev;
    });
  };

  // handle slider changes
  const handleSliderChangeOgr = event => {
    const { name, value } = event.target;
    setFormValues(prevFormValues => ({
      ...prevFormValues,
      [name]: value.toFixed(1) / 100,
    }));
  };

  // Maps

  const productTypeValueMap = productTypes?.map(item => ({
    value: item.id,
    label: item.name,
  }));

  const productTypeMenuItemMap = productTypeValueMap?.map(item => (
    <MenuItem value={item.value} key={item.value}>
      {item.label}
    </MenuItem>
  ));

  const productStatussesItemMap = productStatusses?.map(item => (
    <MenuItem value={item.value} key={item.value}>
      {item.label}
    </MenuItem>
  ));

  // database calls
  const submitProduct = async () => {
    setLoading(true);
    try {
      // map
      const productToValidate = {
        ...formValues,
        suppliers_master_id: supplierId,
        customer_price_vat_excl: formValues.customer_price_vat_excl / 1.15,
      };

      // validate
      const validationResult = await productFormValidation(productToValidate);

      if (validationResult.error) {
        throw validationResult.error;
      }
      // upsert
      const upsertProductResult = await upsertProduct(validationResult);

      if (upsertProductResult.error === 0) {
        handleAlert("Success", "success");
        history.push(`/admin/Supplier/${supplierId}`);
      } else {
        throw upsertProductResult.error;
      }
    } catch (err) {
      if (typeof err === "string") {
        handleAlert(err, "error");
      }
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const updateProduct = async () => {
    if (productId) {
      const getProductResults = await getProduct(productId);

      if (getProductResults.suppliers_master_id === supplierId) {
        setFormValues({
          ...getProductResults,
          customer_price_vat_excl: (
            getProductResults.customer_price_vat_excl * 1.15
          ).toFixed(2),
        });
      }
    }
  };

  const updateProductStatusses = async () => {
    const fetchProductStatussesResult = await fetchProductStatusses();
    if (fetchProductStatussesResult.error === 0) {
      setProductStatusses(() => {
        const psMap = fetchProductStatussesResult.data.map(item => ({
          value: item.id,
          label: item.status_name,
        }));

        return psMap;
      });
    } else {
      throw new Error(fetchProductStatussesResult.error);
    }
  };

  const fetchAllData = async () => {
    setLoading(true);
    await updateProduct();
    await updateProductStatusses();
    setLoading(false);
  };

  // Side Effects
  useEffect(() => {
    try {
      fetchAllData();
    } catch (err) {
      if (typeof err === "string") {
        handleAlert(err, "error");
      }
    }
  }, [productId]);

  return (
    <div>
      {" "}
      <BackLink />
      {loading ? (
        <Typography>Loading...</Typography>
      ) : (
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant="h3" style={{ fontWeight: "bold" }}>
              {productId ? "Edit Product" : "Add Product"}
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="h4">General</Typography>
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Product"
              placeholder="Product"
              variant="outlined"
              required
              name="display_name"
              value={formValues.display_name}
              onChange={handleChange}
              fullWidth
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Product Description"
              variant="outlined"
              required
              name="product_description"
              value={formValues.product_description}
              onChange={handleChange}
              fullWidth
            />
          </Grid>
          <Grid item xs={12}>
            <FormControl fullWidth variant="outlined" required>
              <InputLabel id="product-type-label">Product Type</InputLabel>
              <Select
                value={formValues.refs_product_type_id}
                fullWidth
                variant="outlined"
                required
                name="refs_product_type_id"
                onChange={handleChange}
                label="Product Type"
              >
                {productTypeMenuItemMap}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <FormControl fullWidth variant="outlined" required>
              <InputLabel id="status-label">Status</InputLabel>
              <Select
                value={formValues.refs_product_status_id}
                fullWidth
                variant="outlined"
                required
                name="refs_product_status_id"
                onChange={handleChange}
                label="Status"
              >
                {productStatussesItemMap}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="h4">Commissions</Typography>
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Customer Price VAT Inclusive"
              variant="outlined"
              required
              name="customer_price_vat_excl"
              value={formValues.customer_price_vat_excl}
              onChange={e => handleNumber(e, true)}
              fullWidth
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Total Commission Receivable VAT Exclusive"
              variant="outlined"
              required
              name="com_once_off_receivable_vat_excl"
              value={formValues.com_once_off_receivable_vat_excl}
              onChange={e => handleNumber(e, true)}
              fullWidth
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Revenue points"
              variant="outlined"
              required
              name="revenue_points"
              value={formValues.revenue_points}
              onChange={e => handleNumber(e)}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={8}>
            <Typography id="continuous-slider" gutterBottom>
              OGR %
            </Typography>
            <Slider
              name="com_ogr_percentage_receivable"
              value={
                formValues.com_ogr_percentage_receivable
                  ? (formValues.com_ogr_percentage_receivable * 100).toFixed(1)
                  : 0
              }
              aria-label="com_ogr_percentage_receivable"
              aria-valuetext="com_ogr_percentage_receivable"
              onChange={handleSliderChangeOgr}
              aria-labelledby="continuous-slider"
              valueLabelDisplay="auto"
              min={0}
              max={20}
              step={0.5}
              color="primary"
            />
          </Grid>{" "}
          <Grid item xs={12}>
            <Button
              fullWidth
              variant="contained"
              color="primary"
              onClick={submitProduct}
            >
              Submit
            </Button>
          </Grid>
        </Grid>
      )}
      <Snackbar
        open={alertMessage.open}
        onClose={closeAlert}
        autoHideDuration={5000}
      >
        <Alert severity={alertMessage.severity}>{alertMessage.message}</Alert>
      </Snackbar>
    </div>
  );
}
